import React, { useMemo } from 'react';
import { ChevronDown, ChevronRight, MapPin } from 'lucide-react';

/* Utilidades de agrupación Área → Partida para el catálogo de conceptos */
export const SIN_AREA = 'Sin área';
export const SIN_PARTIDA = 'Sin partida';

export const hasAreas = (concepts) => concepts.some((c) => (c.area || '').trim());

// { [area]: { [partida]: Concept[] } } respetando el orden de aparición
export const groupConcepts = (concepts) => {
  const groups = {};
  concepts.forEach((c) => {
    const area = (c.area || '').trim() || SIN_AREA;
    const partida = (c.partida || '').trim() || SIN_PARTIDA;
    groups[area] = groups[area] || {};
    groups[area][partida] = groups[area][partida] || [];
    groups[area][partida].push(c);
  });
  return groups;
};

// Aplica el filtro { areas: Set, partidas: Set } (vacío = sin filtro)
export const filterByGroup = (concepts, filter) => {
  if (!filter || (!filter.areas?.size && !filter.partidas?.size)) return concepts;
  return concepts.filter((c) => {
    const area = (c.area || '').trim() || SIN_AREA;
    const partida = (c.partida || '').trim() || SIN_PARTIDA;
    if (filter.areas?.size && !filter.areas.has(area)) return false;
    if (filter.partidas?.size && !filter.partidas.has(`${area}||${partida}`)) return false;
    return true;
  });
};

/* Selector reutilizable: checkbox por Área (selecciona todas sus partidas) y por Partida.
   value = { areas: Set<string>, partidas: Set<"area||partida"> }; onChange(newValue) */
export const ConceptGroupPicker = ({ concepts, value, onChange, compact = false }) => {
  const groups = useMemo(() => groupConcepts(concepts), [concepts]);
  const [openAreas, setOpenAreas] = React.useState({});
  const areas = value?.areas || new Set();
  const partidas = value?.partidas || new Set();

  const toggleArea = (area) => {
    const nextAreas = new Set(areas);
    const nextPartidas = new Set(partidas);
    if (nextAreas.has(area)) {
      nextAreas.delete(area);
      Object.keys(groups[area]).forEach((p) => nextPartidas.delete(`${area}||${p}`));
    } else {
      nextAreas.add(area);
      Object.keys(groups[area]).forEach((p) => nextPartidas.delete(`${area}||${p}`));
    }
    onChange({ areas: nextAreas, partidas: nextPartidas });
  };

  const togglePartida = (area, partida) => {
    const key = `${area}||${partida}`;
    const nextAreas = new Set(areas);
    const nextPartidas = new Set(partidas);
    if (nextPartidas.has(key)) nextPartidas.delete(key);
    else { nextPartidas.add(key); nextAreas.add(area); }
    // Si ya no queda ninguna partida marcada del área y el área se marcó por partida, quitarla
    const anyPartida = Object.keys(groups[area]).some((p) => nextPartidas.has(`${area}||${p}`));
    if (!anyPartida && !areas.has(area)) nextAreas.delete(area);
    onChange({ areas: nextAreas, partidas: nextPartidas });
  };

  const count = (list) => list.length;

  return (
    <div className={`rounded-lg border border-[#1E3550] bg-[#0A1422] ${compact ? 'p-2' : 'p-3'}`} data-testid="concept-group-picker">
      {Object.entries(groups).map(([area, byPartida]) => {
        const total = Object.values(byPartida).reduce((a, l) => a + l.length, 0);
        const open = openAreas[area] ?? !compact;
        const partidaKeys = Object.keys(byPartida);
        const showPartidas = partidaKeys.length > 1 || partidaKeys[0] !== SIN_PARTIDA;
        return (
          <div key={area} className="mb-1 last:mb-0" data-testid={`group-area-${area}`}>
            <div className="flex items-center gap-2">
              {showPartidas ? (
                <button type="button" onClick={() => setOpenAreas((o) => ({ ...o, [area]: !open }))} className="text-[#5A7A9A] hover:text-[#E2EAF4]" data-testid={`toggle-area-${area}`}>
                  {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
              ) : <span className="w-4" />}
              <label className="flex items-center gap-2 cursor-pointer text-sm text-[#E2EAF4] font-semibold">
                <input type="checkbox" checked={areas.has(area)} onChange={() => toggleArea(area)} className="accent-[#00CFFF]" data-testid={`check-area-${area}`} />
                <MapPin className="w-3.5 h-3.5 text-[#00CFFF]" />
                {area}
                <span className="text-[10px] font-normal text-[#5A7A9A]">{total} concepto{total !== 1 ? 's' : ''}</span>
              </label>
            </div>
            {showPartidas && open && (
              <div className="ml-10 mt-1 space-y-0.5">
                {partidaKeys.map((partida) => (
                  <label key={partida} className="flex items-center gap-2 cursor-pointer text-xs text-[#E2EAF4]">
                    <input
                      type="checkbox"
                      checked={areas.has(area) || partidas.has(`${area}||${partida}`)}
                      disabled={areas.has(area)}
                      onChange={() => togglePartida(area, partida)}
                      className="accent-[#A855F7]"
                      data-testid={`check-partida-${area}-${partida}`}
                    />
                    {partida}
                    <span className="text-[10px] text-[#5A7A9A]">{count(byPartida[partida])}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ConceptGroupPicker;
